/**
 * Erasure API client.
 *
 * Maps the handoff's mock ``erasure.*`` actions to the real backend:
 *   request()         → POST /family/auth_challenge then POST /family/erasure
 *   cancel()          → POST /family/erasure/cancel  (200 ok / 410 too late)
 *   emailChallenge()  → POST /family/erasure/email-challenge  (always 202)
 *   emailConfirm()    → POST /family/erasure/email-confirm    (token → schedule)
 *
 * The in-app path is two-step (auth challenge + schedule) because the
 * server uses a 5-minute challenge token, not an inline ``{password}`` on
 * the schedule call itself. The store hides that from components.
 */

import { request } from "../api";

export interface ScheduledErasureResponse {
  status: string;
  grace_period_days: number;
  scheduled_deletion_at: string;
}

export class PasswordMismatchError extends Error {
  constructor() {
    super("password did not match");
    this.name = "PasswordMismatchError";
  }
}

export class GraceAlreadyElapsedError extends Error {
  constructor() {
    super("the grace window already closed");
    this.name = "GraceAlreadyElapsedError";
  }
}

export class InvalidTokenError extends Error {
  constructor() {
    super("link expired or already used");
    this.name = "InvalidTokenError";
  }
}

async function authChallenge(password: string): Promise<void> {
  try {
    await request("/family/auth_challenge", {
      method: "POST",
      body: JSON.stringify({ password }),
    });
  } catch (err) {
    const msg = (err as Error).message;
    if (msg.includes("403") || msg.toLowerCase().includes("password")) {
      throw new PasswordMismatchError();
    }
    throw err;
  }
}

export async function requestErasure(
  password: string,
): Promise<ScheduledErasureResponse> {
  await authChallenge(password);
  return request<ScheduledErasureResponse>("/family/erasure", { method: "POST" });
}

export async function cancelErasure(): Promise<void> {
  try {
    await request("/family/erasure/cancel", { method: "POST" });
  } catch (err) {
    const msg = (err as Error).message;
    if (msg.includes("410")) throw new GraceAlreadyElapsedError();
    throw err;
  }
}

/**
 * Auth-less /account/delete: ask the server to email a single-use link.
 * Always resolves — the response does not reveal whether ``email`` maps
 * to an account.
 */
export async function emailChallenge(email: string): Promise<void> {
  await request("/family/erasure/email-challenge", {
    method: "POST",
    body: JSON.stringify({ email }),
  });
}

export async function emailConfirm(
  token: string,
): Promise<ScheduledErasureResponse> {
  try {
    return await request<ScheduledErasureResponse>(
      "/family/erasure/email-confirm",
      { method: "POST", body: JSON.stringify({ token }) },
    );
  } catch (err) {
    const msg = (err as Error).message;
    if (
      msg.includes("403")
      || msg.toLowerCase().includes("invalid")
      || msg.toLowerCase().includes("expired")
    ) {
      throw new InvalidTokenError();
    }
    throw err;
  }
}
